import { Alert, Button, Card, DatePicker, Form, Input, Modal, Select, Space, Table, Tag, message } from 'antd';
import dayjs, { type Dayjs } from 'dayjs';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import ReportDocumentActions from '@/components/finance/report-document/ReportDocumentActions';

type FeeDemand = {
  id: string;
  admission_no?: string;
  first_name?: string;
  last_name?: string;
  fee_head_code?: string;
  fee_head_name?: string;
  period_key: string;
  amount: number;
  status: string;
  sales_invoice_id?: string | null;
  receipt_voucher_id?: string | null;
  student_id: string;
  due_on?: string | null;
};

type FeeStructure = { id: string; code: string; name: string; is_active?: number | boolean };

export default function FeeDemandsPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canDemand = Boolean(
    user?.permissions?.includes('education.fee.demand') || user?.permissions?.includes('education.admin')
  );
  const canCollect = Boolean(
    user?.permissions?.includes('education.fee.collect') || user?.permissions?.includes('education.admin')
  );
  const canPrint = Boolean(
    user?.permissions?.includes('education.fee.view') ||
      user?.permissions?.includes('education.fee.collect') ||
      user?.permissions?.includes('education.fee.manage') ||
      user?.permissions?.includes('education.admin')
  );
  // PATCH /education/fees/demands/:id/due-on is gated by education.fee.manage on the backend.
  const canManage = Boolean(
    user?.permissions?.includes('education.fee.manage') || user?.permissions?.includes('education.admin')
  );
  const [dueOnTarget, setDueOnTarget] = useState<FeeDemand | null>(null);
  const [dueOnValue, setDueOnValue] = useState<Dayjs | null>(null);

  const demands = useQuery({
    queryKey: ['education-fee-demands'],
    queryFn: () => api.get<FeeDemand[]>('/education/fees/demands'),
  });
  const structures = useQuery({
    queryKey: ['education-fee-structures'],
    queryFn: () => api.get<FeeStructure[]>('/education/fees/structures'),
    enabled: canDemand,
  });

  const refresh = () => queryClient.invalidateQueries({ queryKey: ['education-fee-demands'] });

  const generate = useMutation({
    mutationFn: (values: {
      structure_id: string;
      period_key: string;
      grade_level_id?: string;
      due_on?: Dayjs | null;
    }) =>
      api.post('/education/fees/demands/generate', {
        ...values,
        due_on: values.due_on ? values.due_on.format('YYYY-MM-DD') : undefined,
      }),
    onSuccess: (result) => {
      message.success(
        t('feeDemandsGenerated', {
          created: (result.data as { created?: number })?.created ?? 0,
          reused: (result.data as { reused?: number })?.reused ?? 0,
        })
      );
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const invoice = useMutation({
    mutationFn: (demandId: string) => api.post(`/education/fees/demands/${demandId}/invoice`, {}),
    onSuccess: () => {
      message.success(t('feeInvoiceCreated'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const bulkInvoice = useMutation({
    mutationFn: (values: { period_key?: string; limit?: number }) =>
      api.post('/education/fees/demands/invoice-bulk', {
        period_key: values.period_key || undefined,
        limit: values.limit ?? 25,
      }),
    onSuccess: (result) => {
      const body = result.data as { succeeded?: number; failed?: number; attempted?: number };
      message.success(
        t('feeBulkInvoiceDone', {
          succeeded: body?.succeeded ?? 0,
          failed: body?.failed ?? 0,
          attempted: body?.attempted ?? 0,
        })
      );
      refresh();
      queryClient.invalidateQueries({ queryKey: ['education-fee-outstanding'] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const updateDueOn = useMutation({
    mutationFn: (values: { demandId: string; due_on: string | null }) =>
      api.patch(`/education/fees/demands/${values.demandId}/due-on`, { due_on: values.due_on }),
    onSuccess: () => {
      message.success(t('feeDueOnSaved', { defaultValue: 'Due date updated' }));
      setDueOnTarget(null);
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const openDueOnEditor = (row: FeeDemand) => {
    setDueOnTarget(row);
    setDueOnValue(row.due_on ? dayjs(String(row.due_on).slice(0, 10)) : null);
  };

  const linkReceipt = useMutation({
    mutationFn: (values: { demandId: string; receipt_voucher_id: string }) =>
      api.post(`/education/fees/demands/${values.demandId}/receipt`, {
        receipt_voucher_id: values.receipt_voucher_id,
      }),
    onSuccess: () => {
      message.success(t('feeReceiptLinked'));
      refresh();
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  return (
    <EducationPageChrome
      title={t('feeDemands')}
      description={t('feeDemandsIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate('/education/fees/outstanding')}>{t('feeOutstanding')}</Button>
          <Button onClick={() => navigate('/education/fees')}>{t('backToFees')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {canDemand ? (
          <Card size="small" title={t('generateFeeDemands')}>
            <Form layout="inline" onFinish={(values) => generate.mutate(values)}>
              <Form.Item name="structure_id" rules={[{ required: true }]} label={t('feeStructure')}>
                <Select
                  style={{ minWidth: 280 }}
                  options={(structures.data?.data ?? [])
                    .filter((s) => !(s.is_active === 0 || s.is_active === false))
                    .map((s) => ({
                      value: s.id,
                      label: `${s.code} — ${s.name}`,
                    }))}
                  showSearch
                  optionFilterProp="label"
                />
              </Form.Item>
              <Form.Item name="period_key" rules={[{ required: true }]} label={t('periodKey')}>
                <Input placeholder="2026-Q1" style={{ width: 140 }} />
              </Form.Item>
              <Form.Item name="due_on" label={t('dueOn')}>
                <DatePicker format="YYYY-MM-DD" style={{ width: 150 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={generate.isPending}>
                {t('generate')}
              </Button>
            </Form>
          </Card>
        ) : null}

        {canDemand ? (
          <Card size="small" title={t('bulkCreateInvoices')}>
            <Form
              layout="inline"
              initialValues={{ period_key: '2026-Q1', limit: 25 }}
              onFinish={(values) => bulkInvoice.mutate(values)}
            >
              <Form.Item name="period_key" label={t('periodKey')}>
                <Input placeholder="2026-Q1" style={{ width: 140 }} />
              </Form.Item>
              <Form.Item name="limit" label={t('batchLimit', { defaultValue: 'Batch size' })}>
                <Input type="number" min={1} max={100} style={{ width: 100 }} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={bulkInvoice.isPending}>
                {t('bulkCreateInvoices')}
              </Button>
            </Form>
          </Card>
        ) : null}

        <Card size="small">
          <Table
            rowKey="id"
            size="small"
            loading={demands.isLoading}
            dataSource={demands.data?.data ?? []}
            pagination={{ pageSize: 20 }}
            columns={[
              {
                title: t('student'),
                render: (_: unknown, row: FeeDemand) =>
                  `${row.admission_no || ''} — ${[row.first_name, row.last_name].filter(Boolean).join(' ')}`,
              },
              {
                title: t('feeHead'),
                render: (_: unknown, row: FeeDemand) =>
                  `${row.fee_head_code || ''} — ${row.fee_head_name || ''}`,
              },
              { title: t('periodKey'), dataIndex: 'period_key', width: 110 },
              { title: t('amount'), dataIndex: 'amount', width: 100 },
              {
                title: t('dueOn'),
                dataIndex: 'due_on',
                width: 120,
                render: (v?: string | null) => (v ? String(v).slice(0, 10) : '—'),
              },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('actions'),
                width: 340,
                render: (_: unknown, row: FeeDemand) => (
                  <Space wrap>
                    <Button size="small" onClick={() => navigate(`/education/fees/students/${row.student_id}`)}>
                      {t('ledger')}
                    </Button>
                    {canManage && row.status === 'open' && !row.sales_invoice_id && !row.receipt_voucher_id ? (
                      <Button size="small" onClick={() => openDueOnEditor(row)}>
                        {t('feeEditDueOn', { defaultValue: 'Edit due date' })}
                      </Button>
                    ) : null}
                    {canDemand && !row.sales_invoice_id ? (
                      <Button
                        size="small"
                        type="primary"
                        loading={invoice.isPending}
                        onClick={() => invoice.mutate(row.id)}
                      >
                        {t('createInvoice')}
                      </Button>
                    ) : null}
                    {canCollect && row.sales_invoice_id && !row.receipt_voucher_id ? (
                      <Button
                        size="small"
                        onClick={() => {
                          const receiptId = window.prompt(t('receiptVoucherIdPrompt'));
                          if (receiptId) {
                            linkReceipt.mutate({ demandId: row.id, receipt_voucher_id: receiptId.trim() });
                          }
                        }}
                      >
                        {t('linkReceipt')}
                      </Button>
                    ) : null}
                    {canPrint && row.status !== 'cancelled' && row.status !== 'waived' ? (
                      <ReportDocumentActions
                        reportKey="education-fee-receipt"
                        params={{ id: row.id }}
                        documentId={row.id}
                        size="small"
                        allowPreprinted
                        sendPermissions={['education.fee.collect', 'education.fee.manage', 'education.admin']}
                        emailLabel={t('email', { defaultValue: 'Email' })}
                      />
                    ) : null}
                  </Space>
                ),
              },
            ]}
          />
        </Card>
      </Space>
      <Modal
        open={Boolean(dueOnTarget)}
        title={t('feeDueOnTitle', { defaultValue: 'Edit due date' })}
        okText={t('save')}
        cancelText={t('cancel')}
        confirmLoading={updateDueOn.isPending}
        onCancel={() => setDueOnTarget(null)}
        onOk={() =>
          dueOnTarget &&
          updateDueOn.mutate({
            demandId: dueOnTarget.id,
            due_on: dueOnValue ? dueOnValue.format('YYYY-MM-DD') : null,
          })
        }
        destroyOnClose
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <Alert
            type="info"
            showIcon
            message={t('feeDueOnHint', {
              defaultValue:
                'Leave empty to clear the date; the linked invoice due date then applies. Only open demands without an invoice can be changed.',
            })}
          />
          <DatePicker
            allowClear
            format="YYYY-MM-DD"
            style={{ width: '100%' }}
            value={dueOnValue}
            onChange={(value) => setDueOnValue(value)}
          />
        </Space>
      </Modal>
    </EducationPageChrome>
  );
}
